import React, { useState, useMemo } from 'react';
import type { TopClientOutstanding } from '../../../hooks/useOutstandingCollections';
import {
  applyTopClientsFilter,
  fmtUSD,
  type OutstandingFilter,
  type SortBy,
} from '../../../utils/outstandingUtils';

interface TopClientsTabProps {
  clients: TopClientOutstanding[];
}

// ─── Filter button ────────────────────────────────────────────────────────────
const FilterBtn: React.FC<{
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}> = ({ active, onClick, children }) => (
  <button
    onClick={onClick}
    className={`text-[11px] font-semibold px-3 py-1.5 rounded-lg border transition-colors ${
      active
        ? 'bg-blue-600 text-white border-blue-600'
        : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
    }`}
  >
    {children}
  </button>
);

// ─── Client rank badge ────────────────────────────────────────────────────────
function getRankStyle(i: number): string {
  if (i === 0) return 'bg-red-100 text-red-700';
  if (i === 1) return 'bg-amber-100 text-amber-700';
  if (i === 2) return 'bg-amber-100 text-amber-700';
  if (i < 5) return 'bg-sky-100 text-sky-700';
  return 'bg-slate-100 text-slate-500';
}

// ─── Client row ───────────────────────────────────────────────────────────────
const ClientRow: React.FC<{
  client: TopClientOutstanding;
  index: number;
  totalOutstanding: number;
}> = ({ client, index, totalOutstanding }) => {
  const pct = totalOutstanding > 0 ? (client.total_outstanding / totalOutstanding) * 100 : 0;
  const daysDisplay =
    client.avg_days_overdue < 0
      ? `${Math.abs(Math.round(client.avg_days_overdue))}d remaining`
      : `${Math.round(client.avg_days_overdue)}d overdue`;
  const daysColor = client.avg_days_overdue > 90 ? 'text-red-700' : client.avg_days_overdue > 30 ? 'text-amber-700' : client.avg_days_overdue > 0 ? 'text-orange-600' : 'text-blue-700';
  const deposit = Number(client.net_deposit_balance) || 0;
  const hasDeposit = deposit > 0;

  return (
    <div className="border-b border-slate-100 last:border-b-0">
      <div className="flex items-start gap-3 px-4 py-3 hover:bg-slate-50 transition-colors">
        {/* Rank badge */}
        <div className={`w-7 h-7 rounded-lg flex items-center justify-center text-[11px] font-bold flex-none shrink-0 ${getRankStyle(index)}`}>
          #{index + 1}
        </div>

        {/* Client info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="text-sm font-semibold text-slate-800 leading-tight">
              {client.contact_name.trim()}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
            <span className="text-[11px] text-slate-500">
              {client.outstanding_invoice_count} invoices
            </span>
            <span className="text-[10px] text-slate-300">·</span>
            <span className={`text-[11px] font-medium ${daysColor}`}>{daysDisplay}</span>
          </div>
        </div>

        <div className="flex items-center gap-3 shrink-0 self-center">
          {/* Outstanding amount */}
          <div className="text-right w-[7.25rem]">
            <p className="text-sm font-bold text-red-700 tabular-nums leading-tight">
              {fmtUSD(client.total_outstanding)}
            </p>
            <p className="text-[11px] text-slate-500 tabular-nums mt-0.5">
              {pct.toFixed(1)}% of total
            </p>
          </div>

          {/* Deposit — own column, highlighted */}
          <div
            className={`text-right w-[6.75rem] min-h-[2.75rem] flex flex-col items-end justify-center rounded-lg border px-2.5 py-1.5 ${
              hasDeposit
                ? 'bg-slate-100 border-slate-300 shadow-sm'
                : 'bg-slate-50/80 border-slate-100 border-dashed'
            }`}
          >
            {hasDeposit ? (
              <>
                <span className="text-[9px] font-bold uppercase tracking-widest text-emerald-700">
                  Deposit
                </span>
                <span className="text-sm font-bold tabular-nums text-emerald-800 leading-tight mt-0.5">
                  {fmtUSD(deposit)}
                </span>
              </>
            ) : (
              <span className="text-[11px] text-slate-400 tabular-nums">—</span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Main Tab ─────────────────────────────────────────────────────────────────
export const TopClientsTab: React.FC<TopClientsTabProps> = ({ clients }) => {
  const [filter, setFilter] = useState<OutstandingFilter>('all');
  const [sortBy, setSortBy] = useState<SortBy>('outstanding');
  const [showAll, setShowAll] = useState(false);

  const filteredClients = useMemo(
    () => applyTopClientsFilter(clients, filter, sortBy),
    [clients, filter, sortBy]
  );

  const displayClients = showAll ? filteredClients : filteredClients.slice(0, 20);
  const totalOfFiltered = filteredClients.reduce((s, c) => s + c.total_outstanding, 0);

  return (
    <div className="space-y-4">
      {/* Filters + sort */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-md overflow-hidden">
        <div className="flex items-center gap-2 px-4 pt-3 pb-2.5 border-b border-slate-100 flex-wrap gap-y-2">
          <div className="w-0.5 h-4 rounded-full bg-orange-500 flex-none" />
          <span className="text-[10px] font-bold uppercase tracking-widest text-slate-600">
            Top Clients by Outstanding
          </span>
          <div className="ml-auto flex items-center gap-2 flex-wrap">
            {/* Filter buttons */}
            <div className="flex items-center gap-1 flex-wrap">
              {([
                ['all', 'All Clients'],
                ['critical_risk', 'Critical Risk'],
                ['high_priority', 'High Priority'],
                ['overdue_90_plus', '90+ Days'],
                ['overdue_31_90', '31-90 Days'],
                ['overdue_1_30', '1-30 Days'],
              ] as [OutstandingFilter, string][]).map(([val, lbl]) => (
                <FilterBtn key={val} active={filter === val} onClick={() => setFilter(val)}>{lbl}</FilterBtn>
              ))}
            </div>
            {/* Sort */}
            <div className="flex items-center gap-1 border-l border-slate-200 pl-2 ml-1">
              <span className="text-[10px] text-slate-500 font-semibold uppercase tracking-wider">Sort:</span>
              <FilterBtn active={sortBy === 'outstanding'} onClick={() => setSortBy('outstanding')}>Amount</FilterBtn>
              <FilterBtn active={sortBy === 'days_overdue'} onClick={() => setSortBy('days_overdue')}>Days Overdue</FilterBtn>
            </div>
          </div>
        </div>

        {/* Results count */}
        <div className="px-4 py-2 bg-slate-50 border-b border-slate-100 text-[11px] text-slate-600">
          Showing <span className="font-semibold">{displayClients.length}</span> of{' '}
          <span className="font-semibold">{filteredClients.length}</span> clients ·{' '}
          Total: <span className="font-semibold">{fmtUSD(totalOfFiltered)}</span>
        </div>

        {/* Client list */}
        {filteredClients.length === 0 ? (
          <div className="px-4 py-10 text-center text-slate-500 text-sm">
            No clients match the selected filter.
          </div>
        ) : (
          <>
            {displayClients.map((client, i) => (
              <ClientRow
                key={client.contact_id}
                client={client}
                index={i}
                totalOutstanding={totalOfFiltered}
              />
            ))}
            {filteredClients.length > 20 && (
              <div className="px-4 py-3 border-t border-slate-100">
                <button
                  onClick={() => setShowAll(v => !v)}
                  className="w-full text-[12px] font-semibold text-blue-600 hover:text-blue-700 py-1.5 rounded-lg hover:bg-blue-50 transition-colors"
                >
                  {showAll
                    ? 'Show fewer'
                    : `Show all ${filteredClients.length} clients`}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default TopClientsTab;
